
import React from 'react';
import { Heart, Brain, Sparkles, Clock, Award, Music } from 'lucide-react';

interface BenefitProps {
  icon: React.ReactNode;
  title: string;
  description: string;
}

const Benefit = ({ icon, title, description }: BenefitProps) => {
  return (
    <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100 transition-transform hover:translate-y-[-5px]">
      <div className="w-12 h-12 flex items-center justify-center bg-piano-gold/10 text-piano-gold rounded-full mb-4">
        {icon}
      </div>
      <h3 className="text-xl font-serif font-semibold mb-3">{title}</h3>
      <p className="text-gray-600">{description}</p>
    </div>
  );
};

const ChildrenBenefits = () => {
  const benefits = [
    {
      icon: <Heart className="w-6 h-6" />,
      title: "Livslang kærlighed til musik",
      description: "Skaber et positivt forhold til musik, der kan vare hele livet og være en kilde til glæde og kreativitet."
    },
    {
      icon: <Brain className="w-6 h-6" />,
      title: "Kognitiv udvikling",
      description: "Styrker hjernens udvikling, herunder koncentrationsevne, hukommelse og problemløsningsfærdigheder."
    },
    {
      icon: <Sparkles className="w-6 h-6" />,
      title: "Kreativ udfoldelse",
      description: "Giver børn et kreativt udtryk og en mulighed for at udtrykke følelser gennem musikkens sprog."
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: "Tålmodighed og disciplin",
      description: "Lærer børn værdien af regelmæssig øvning, tålmodighed og at arbejde mod langsigtede mål."
    },
    {
      icon: <Award className="w-6 h-6" />,
      title: "Selvtillid og stolthed",
      description: "Opbygger selvtillid når barnet mestrer nye færdigheder og optræder for familie og venner."
    },
    {
      icon: <Music className="w-6 h-6" />,
      title: "Musikalsk grundlag",
      description: "Giver et solidt fundament i musikteori og teknik, der kan bruges til ethvert instrument senere i livet."
    }
  ];

  return (
    <section className="py-16 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-serif font-bold mb-4">
            Hvorfor vælge klaverundervisning til dit barn?
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Klaverundervisning giver dit barn meget mere end bare musikalske færdigheder. Det er en investering i deres kreative, kognitive og personlige udvikling.
          </p>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {benefits.map((benefit, index) => (
            <Benefit 
              key={index}
              icon={benefit.icon}
              title={benefit.title}
              description={benefit.description}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ChildrenBenefits;
